// ==========================================================
// Workbench Bottom Tool Palette & Color Selector
// ==========================================================

import type { ToolType } from '../engine/PaintBrush';
import { sounds } from '../audio/SoundEffects';
import { i18n } from '../i18n/I18nService';

export class ToolPaletteController {
  private activeTool: ToolType = 'brush';
  private activeColor: string = '#2B82C9';
  private activeSize: number = 20;

  private swatchesContainer: HTMLElement;
  private sizeSlider: HTMLInputElement;
  private toolButtons: NodeListOf<HTMLButtonElement>;
  private sizeButtons: NodeListOf<HTMLButtonElement>;

  // Callbacks
  private onToolChange?: (tool: ToolType) => void;
  private onColorChange?: (color: string) => void;
  private onSizeChange?: (size: number) => void;
  private onPeelAllTapes?: () => void;
  private onUndo?: () => void;
  private onClear?: () => void;
  private onOpenColorLab?: () => void;

  constructor() {
    this.swatchesContainer = document.getElementById('color-swatches')!;
    this.sizeSlider = document.getElementById('brush-size-slider') as HTMLInputElement;
    this.toolButtons = document.querySelectorAll('.tool-btn');
    this.sizeButtons = document.querySelectorAll('.size-btn');

    this.initEvents();
    this.updateLanguageUI();

    i18n.subscribe(() => {
      this.updateLanguageUI();
    });
  }

  private initEvents() {
    // 1. Tool Selection
    this.toolButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        sounds.playClick();
        const tool = btn.getAttribute('data-tool') as ToolType;
        this.setActiveTool(tool);
      });
    });

    // 2. Peel All Tapes Quick Action Button
    document.getElementById('btn-peel-all')?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onPeelAllTapes) this.onPeelAllTapes();
    });

    // 3. Brush Size Presets
    this.sizeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        sounds.playClick();
        const size = parseInt(btn.getAttribute('data-size') || '20', 10);
        this.setActiveSize(size);
      });
    });

    // 4. Brush Size Slider
    this.sizeSlider.addEventListener('input', () => {
      const size = parseInt(this.sizeSlider.value, 10);
      this.setActiveSize(size, false);
    });

    // 5. Undo & Clear
    document.getElementById('btn-undo')?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onUndo) this.onUndo();
    });

    document.getElementById('btn-clear-paint')?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onClear) this.onClear();
    });

    // 6. Open Color Lab
    document.getElementById('btn-open-color-lab')?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onOpenColorLab) this.onOpenColorLab();
    });

    // 7. Workbench Scroll Indicators & Navigation
    const wbFooter = document.getElementById('workbench-footer');
    const btnScrollLeft = document.getElementById('btn-scroll-wb-left');
    const btnScrollRight = document.getElementById('btn-scroll-wb-right');
    const scrollMask = document.getElementById('wb-scroll-hint-mask');

    const updateScrollState = () => {
      if (!wbFooter) return;
      const scrollLeft = wbFooter.scrollLeft;
      const maxScroll = wbFooter.scrollWidth - wbFooter.clientWidth;

      if (scrollLeft > 15) {
        btnScrollLeft?.classList.remove('hidden');
      } else {
        btnScrollLeft?.classList.add('hidden');
      }

      if (maxScroll > 10 && scrollLeft < maxScroll - 15) {
        btnScrollRight?.classList.remove('hidden');
        scrollMask?.classList.remove('hidden');
      } else {
        btnScrollRight?.classList.add('hidden');
        scrollMask?.classList.add('hidden');
      }
    };

    wbFooter?.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    setTimeout(updateScrollState, 300);

    btnScrollLeft?.addEventListener('click', () => {
      sounds.playClick();
      wbFooter?.scrollBy({ left: -180, behavior: 'smooth' });
    });

    btnScrollRight?.addEventListener('click', () => {
      sounds.playClick();
      wbFooter?.scrollBy({ left: 180, behavior: 'smooth' });
    });
  }

  public setCallbacks(callbacks: {
    onToolChange: (tool: ToolType) => void;
    onColorChange: (color: string) => void;
    onSizeChange: (size: number) => void;
    onPeelAllTapes: () => void;
    onUndo: () => void;
    onClear: () => void;
    onOpenColorLab: () => void;
  }) {
    this.onToolChange = callbacks.onToolChange;
    this.onColorChange = callbacks.onColorChange;
    this.onSizeChange = callbacks.onSizeChange;
    this.onPeelAllTapes = callbacks.onPeelAllTapes;
    this.onUndo = callbacks.onUndo;
    this.onClear = callbacks.onClear;
    this.onOpenColorLab = callbacks.onOpenColorLab;
  }

  public setActiveTool(tool: ToolType) {
    this.activeTool = tool;
    this.toolButtons.forEach(btn => {
      if (btn.getAttribute('data-tool') === tool) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    if (this.onToolChange) {
      this.onToolChange(tool);
    }
  }

  public getActiveTool(): ToolType {
    return this.activeTool;
  }

  public setActiveSize(size: number, updateSlider: boolean = true) {
    this.activeSize = size;
    if (updateSlider) {
      this.sizeSlider.value = String(size);
    }

    this.sizeButtons.forEach(btn => {
      const btnSize = parseInt(btn.getAttribute('data-size') || '0', 10);
      if (btnSize === size) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    if (this.onSizeChange) {
      this.onSizeChange(size);
    }
  }

  public getActiveSize(): number {
    return this.activeSize;
  }

  public setActiveColor(color: string) {
    this.activeColor = color;
    const swatches = this.swatchesContainer.querySelectorAll('.color-swatch');
    swatches.forEach(swatch => {
      const swatchColor = swatch.getAttribute('data-color');
      if (swatchColor?.toLowerCase() === color.toLowerCase()) {
        swatch.classList.add('active');
      } else {
        swatch.classList.remove('active');
      }
    });

    if (this.onColorChange) {
      this.onColorChange(color);
    }
  }

  public getActiveColor(): string {
    return this.activeColor;
  }

  public setPaletteColors(colors: string[]) {
    this.swatchesContainer.innerHTML = '';
    colors.forEach((color, idx) => {
      const swatch = document.createElement('button');
      swatch.className = `color-swatch ${idx === 0 ? 'active' : ''}`;
      swatch.style.backgroundColor = color;
      swatch.setAttribute('data-color', color);
      swatch.title = `Color: ${color}`;

      swatch.addEventListener('click', () => {
        sounds.playClick();
        this.setActiveColor(color);
      });

      this.swatchesContainer.appendChild(swatch);
    });

    if (colors.length > 0) {
      this.setActiveColor(colors[0]);
    }
  }

  public addCustomColor(color: string) {
    const existing = this.swatchesContainer.querySelector(`[data-color="${color}"]`);
    if (existing) {
      this.setActiveColor(color);
      return;
    }

    const swatch = document.createElement('button');
    swatch.className = 'color-swatch custom-mixed active';
    swatch.style.backgroundColor = color;
    swatch.setAttribute('data-color', color);
    swatch.title = `Mixed Color: ${color}`;

    swatch.addEventListener('click', () => {
      sounds.playClick();
      this.setActiveColor(color);
    });

    this.swatchesContainer.appendChild(swatch);
    this.setActiveColor(color);
  }

  public updateLanguageUI() {
    const t = i18n.dict.tools;

    // Tool buttons names and titles
    const toolMap: Record<ToolType, { name: string; title: string }> = {
      brush: t.brush,
      roller: t.roller,
      spray: t.spray,
      tape: t.tape,
      peel: t.peel,
      sponge: t.sponge,
      gloss: t.gloss,
      hand: t.hand
    };

    this.toolButtons.forEach(btn => {
      const tool = btn.getAttribute('data-tool') as ToolType;
      const info = toolMap[tool];
      if (info) {
        btn.title = info.title;
        const nameEl = btn.querySelector('.tool-name');
        if (nameEl) nameEl.textContent = info.name;
      }
    });

    const peelAllBtn = document.getElementById('btn-peel-all');
    if (peelAllBtn) {
      peelAllBtn.title = t.peelAll.title;
      const nameEl = peelAllBtn.querySelector('.tool-name');
      if (nameEl) nameEl.textContent = t.peelAll.name;
    }

    // Size label & presets
    const sliderLabel = document.querySelector('.slider-label');
    if (sliderLabel) sliderLabel.textContent = t.sizeLabel;

    const sizeBtns = document.querySelectorAll('.size-btn');
    if (sizeBtns[0]) sizeBtns[0].textContent = t.sizeFine;
    if (sizeBtns[1]) sizeBtns[1].textContent = t.sizeMedium;
    if (sizeBtns[2]) sizeBtns[2].textContent = t.sizeBroad;

    // Lab button & quick actions
    const labText = document.querySelector('.lab-text');
    if (labText) labText.textContent = t.labBtn;

    const undoBtn = document.getElementById('btn-undo');
    if (undoBtn) undoBtn.title = t.undoTitle;

    const clearBtn = document.getElementById('btn-clear-paint');
    if (clearBtn) clearBtn.title = t.clearTitle;
  }
}
